import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, firstValueFrom, timer, fromEvent } from 'rxjs';
import {
  takeUntil,
  filter,
  map,
  share
} from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';
import {
  WebSocketConfig,
  WebSocketMessage,
  QueuedMessage,
  WebSocketEventType,
  WebSocketConnectionStatus,
  PriceUpdateMessage,
  CompetitorPriceMessage,
  PriceAlertMessage,
  MarketChangeMessage,
  AnalyticsUpdateMessage,
  SystemAlertMessage,
  SubscriptionOptions,
  OrderCreatedEvent,
  PriceUpdatedEvent,
  RepricingCompletedEvent,
  CompetitorChangedEvent,
  ServerBroadcastMessage,
  ServerEventMessage,
  DeltaEventsResponse,
} from '../../shared/interfaces/websocket.interfaces';

/**
 * Native WebSocket Service for real-time updates
 * Replaces Socket.IO with native WebSocket API for FastAPI compatibility
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private ws: WebSocket | null = null;
  private destroy$ = new Subject<void>();
  private messageQueue: QueuedMessage[] = [];
  private reconnectAttempts = 0;
  private isReconnecting = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private config: WebSocketConfig;
  private currentToken?: string;
  private lastEventId?: string;
  private lastEventSequence = 0;

  // Connection status
  private connectionStatus$ = new BehaviorSubject<WebSocketConnectionStatus>({
    isConnected: false,
    connectionAttempts: 0,
    isReconnecting: false,
    queuedMessages: 0
  });

  // Message streams
  private priceUpdates$ = new Subject<PriceUpdateMessage>();
  private competitorPrices$ = new Subject<CompetitorPriceMessage>();
  private priceAlerts$ = new Subject<PriceAlertMessage>();
  private marketChanges$ = new Subject<MarketChangeMessage>();
  private analyticsUpdates$ = new Subject<AnalyticsUpdateMessage>();
  private systemAlerts$ = new Subject<SystemAlertMessage>();
  private orderCreated$ = new Subject<OrderCreatedEvent>();
  private priceUpdated$ = new Subject<PriceUpdatedEvent>();
  private repricingCompleted$ = new Subject<RepricingCompletedEvent>();
  private competitorChanged$ = new Subject<CompetitorChangedEvent>();
  private genericMessages$ = new Subject<any>();

  // Batched message streams for performance (batch every second)
  private batchedPriceUpdates$: Observable<PriceUpdateMessage[]>;

  constructor() {
    this.config = {
      url: environment.websocketUrl,
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      timeout: 30000,
      heartbeatInterval: 30000,
      messageQueueSize: 1000
    };

    // Create batched stream
    this.batchedPriceUpdates$ = this.createBatchedStream(this.priceUpdates$, 1000);

    // Auto-reconnect on network connectivity
    if (typeof window !== 'undefined') {
      fromEvent(window, 'online')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.handleNetworkReconnect());
    }
  }

  private createBatchedStream<T>(source$: Subject<T>, intervalMs: number): Observable<T[]> {
    const buffer: T[] = [];
    const output$ = new Subject<T[]>();

    source$.pipe(takeUntil(this.destroy$)).subscribe(item => {
      buffer.push(item);
    });

    timer(0, intervalMs)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (buffer.length > 0) {
          output$.next([...buffer]);
          buffer.length = 0;
        }
      });

    return output$.asObservable().pipe(share());
  }

  get connectionStatus(): Observable<WebSocketConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  get isConnected$(): Observable<boolean> {
    return this.connectionStatus$.pipe(map(status => status.isConnected));
  }

  // Message streams getters
  get priceUpdates(): Observable<PriceUpdateMessage[]> {
    return this.batchedPriceUpdates$;
  }

  get competitorPrices(): Observable<CompetitorPriceMessage> {
    return this.competitorPrices$.asObservable();
  }

  get priceAlerts(): Observable<PriceAlertMessage> {
    return this.priceAlerts$.asObservable();
  }

  get marketChanges(): Observable<MarketChangeMessage> {
    return this.marketChanges$.asObservable();
  }

  get analyticsUpdates(): Observable<AnalyticsUpdateMessage> {
    return this.analyticsUpdates$.asObservable();
  }

  get systemAlerts(): Observable<SystemAlertMessage> {
    return this.systemAlerts$.asObservable();
  }

  get orderCreated(): Observable<OrderCreatedEvent> {
    return this.orderCreated$.asObservable();
  }

  get priceUpdated(): Observable<PriceUpdatedEvent> {
    return this.priceUpdated$.asObservable();
  }

  get repricingCompleted(): Observable<RepricingCompletedEvent> {
    return this.repricingCompleted$.asObservable();
  }

  get competitorChanged(): Observable<CompetitorChangedEvent> {
    return this.competitorChanged$.asObservable();
  }

  /**
   * Connect to WebSocket server
   * @param token Optional JWT token for authentication
   */
  connect(token?: string): void {
    if (
      this.ws?.readyState === WebSocket.OPEN ||
      this.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const authToken = token ?? this.currentToken ?? this.authService.getToken() ?? undefined;
    this.currentToken = authToken;
    this.clearReconnectTimer();
    this.updateConnectionStatus({
      isReconnecting: this.isReconnecting,
      connectionAttempts: this.reconnectAttempts
    });

    try {
      const baseUrl = this.getWebSocketUrl();
      const wsUrl = authToken ? `${baseUrl}?token=${encodeURIComponent(authToken)}` : baseUrl;

      this.ws = new WebSocket(wsUrl);
      this.setupWebSocketHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.handleConnectionError(error);
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      const wasReconnecting =
        this.isReconnecting ||
        this.reconnectAttempts > 0 ||
        Boolean(this.connectionStatus$.value.lastDisconnected);

      console.log('WebSocket connected');
      this.clearReconnectTimer();
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.updateConnectionStatus({
        isConnected: true,
        isReconnecting: false,
        lastConnected: Date.now()
      });
      this.startHeartbeat();
      this.processMessageQueue();

      if (wasReconnecting && this.lastEventId) {
        void this.syncMissedEvents();
      }
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.ws = null;
      this.stopHeartbeat();
      this.updateConnectionStatus({
        isConnected: false,
        lastDisconnected: Date.now()
      });

      // Auto-reconnect unless manually disconnected (code 1000)
      if (event.code !== 1000 && !this.isReconnecting) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.handleConnectionError(error);
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };
  }

  private handleMessage(rawData: string): void {
    try {
      const message = JSON.parse(rawData);
      const messageType = message.type;

      // Handle different message types
      switch (messageType) {
        case 'connected':
          console.log('WebSocket authenticated:', message);
          break;

        case 'pong':
          // Heartbeat response received
          break;

        case 'subscribed':
        case 'unsubscribed':
          console.log(`Channel ${messageType}:`, message.channel);
          break;

        case 'broadcast':
          this.handleBroadcastMessage(message as ServerBroadcastMessage);
          break;

        case 'event':
          this.handleServerEventEnvelope(message as ServerEventMessage);
          break;

        case 'tenant_notification':
        case 'user_notification':
          if (message.data?.type === 'event') {
            this.handleServerEventEnvelope(message.data as ServerEventMessage);
          } else {
            this.genericMessages$.next({ event: messageType, data: message.data });
          }
          break;

        case 'error':
          console.error('WebSocket error message:', message.message);
          break;

        default:
          // Try to route as generic event
          this.routeEventMessage(messageType, message.data || message);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  private handleBroadcastMessage(message: ServerBroadcastMessage): void {
    if (message.data?.type === 'event') {
      this.handleServerEventEnvelope(message.data);
      return;
    }

    this.genericMessages$.next({
      event: 'broadcast',
      channel: message.channel,
      data: message.data,
      timestamp: message.timestamp
    });
  }

  private handleServerEventEnvelope(message: ServerEventMessage): void {
    if (message.id && this.isDuplicateEvent(message.id)) {
      return;
    }

    if (message.id) {
      this.rememberEventId(message.id);
    }

    this.routeEventMessage(message.event, message.data, message.timestamp);
  }

  private routeEventMessage(event: string, data: any, timestamp?: string): void {
    switch (event) {
      case WebSocketEventType.PRICE_UPDATE:
        this.priceUpdates$.next(data);
        break;
      case WebSocketEventType.COMPETITOR_PRICE:
        this.competitorPrices$.next(data);
        break;
      case WebSocketEventType.PRICE_ALERT:
        this.priceAlerts$.next(data);
        break;
      case WebSocketEventType.MARKET_CHANGE:
        this.marketChanges$.next(data);
        break;
      case WebSocketEventType.ANALYTICS_UPDATE:
        this.analyticsUpdates$.next(data);
        break;
      case WebSocketEventType.SYSTEM_ALERT:
        this.systemAlerts$.next(data);
        break;
      case WebSocketEventType.ORDER_CREATED:
        this.orderCreated$.next(this.normalizeOrderCreatedEvent(data, timestamp));
        break;
      case WebSocketEventType.PRICE_UPDATED:
        this.priceUpdated$.next(this.normalizePriceUpdatedEvent(data, timestamp));
        break;
      case WebSocketEventType.REPRICING_COMPLETED:
        this.repricingCompleted$.next(this.normalizeRepricingCompletedEvent(data, timestamp));
        break;
      case WebSocketEventType.COMPETITOR_CHANGED:
        this.competitorChanged$.next(this.normalizeCompetitorChangedEvent(data, timestamp));
        break;
      default:
        this.genericMessages$.next({ event, data, timestamp });
    }
  }

  private handleConnectionError(error: any): void {
    this.updateConnectionStatus({
      isConnected: false,
      isReconnecting: false
    });

    if (!this.isReconnecting) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.updateConnectionStatus({ isReconnecting: false });
      return;
    }

    this.isReconnecting = true;
    const reconnectDelays = [1000, 2000, 4000, 8000];
    const attempt = this.reconnectAttempts;
    const delay = Math.min(reconnectDelays[attempt] ?? 30000, 30000);
    this.reconnectAttempts += 1;

    this.updateConnectionStatus({
      isReconnecting: true,
      connectionAttempts: this.reconnectAttempts
    });

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(async () => {
      if (!this.isReconnecting) {
        return;
      }

      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);
      let token: string | undefined;
      try {
        token = (await this.authService.getFreshToken()) ?? this.currentToken ?? undefined;
      } catch {
        console.warn('[WS] Failed to refresh token, using cached token');
        token = this.currentToken ?? undefined;
      }
      this.connect(token);
    }, delay);
  }

  private handleNetworkReconnect(): void {
    if (this.ws?.readyState !== WebSocket.OPEN && !this.isReconnecting) {
      console.log('Network is back online, attempting to reconnect');
      this.reconnectAttempts = 0;
      (async () => {
        let token: string | undefined;
        try {
          token = (await this.authService.getFreshToken()) ?? this.currentToken ?? undefined;
        } catch {
          console.warn('[WS] Failed to refresh token, using cached token');
          token = this.currentToken ?? undefined;
        }
        this.connect(token);
      })();
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendRaw({ type: 'ping' });
      }
    }, this.config.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private updateConnectionStatus(updates: Partial<WebSocketConnectionStatus>): void {
    const current = this.connectionStatus$.value;
    this.connectionStatus$.next({
      ...current,
      ...updates,
      queuedMessages: this.messageQueue.length
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isReconnecting = false;
    this.stopHeartbeat();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.updateConnectionStatus({
      isConnected: false,
      isReconnecting: false
    });
  }

  /**
   * Send raw message to WebSocket
   */
  private sendRaw(data: any): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  /**
   * Emit event to server
   */
  emit(event: string, data?: any): void {
    const message: WebSocketMessage = {
      id: this.generateMessageId(),
      type: event,
      timestamp: Date.now(),
      data
    };

    if (!this.sendRaw(message)) {
      this.queueMessage(message);
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel: string): void {
    this.sendRaw({
      type: 'subscribe',
      channel
    });
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel: string): void {
    this.sendRaw({
      type: 'unsubscribe',
      channel
    });
  }

  private queueMessage(message: WebSocketMessage): void {
    if (this.messageQueue.length >= this.config.messageQueueSize) {
      this.messageQueue.shift(); // Remove oldest message
    }

    const queuedMessage: QueuedMessage = {
      ...message,
      attempts: 0,
      maxAttempts: 3
    };

    this.messageQueue.push(queuedMessage);
    this.updateConnectionStatus({ queuedMessages: this.messageQueue.length });
  }

  private processMessageQueue(): void {
    const messagesToRetry = [...this.messageQueue];
    this.messageQueue = [];

    messagesToRetry.forEach(message => {
      if (message.attempts < message.maxAttempts) {
        message.attempts++;
        this.emit(message.type, message.data);
      }
    });

    this.updateConnectionStatus({ queuedMessages: 0 });
  }

  private generateMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Subscribe to generic events
   */
  on<T = any>(event: string): Observable<T> {
    return this.genericMessages$.pipe(
      filter(msg => msg.event === event),
      map(msg => msg.data as T)
    );
  }

  // Enhanced subscription methods with options
  subscribeToProducts(productIds: string[], options?: SubscriptionOptions): void {
    this.emit(WebSocketEventType.SUBSCRIBE_PRODUCT, {
      productIds,
      ...options
    });
  }

  unsubscribeFromProducts(productIds: string[]): void {
    this.emit(WebSocketEventType.UNSUBSCRIBE_PRODUCT, { productIds });
  }

  subscribeToDashboard(options?: SubscriptionOptions): void {
    this.emit(WebSocketEventType.SUBSCRIBE_DASHBOARD, options);
  }

  unsubscribeFromDashboard(): void {
    this.emit(WebSocketEventType.UNSUBSCRIBE_DASHBOARD);
  }

  // Legacy methods for backward compatibility
  onPriceChange(): Observable<PriceUpdateMessage> {
    return this.priceUpdates$.asObservable();
  }

  onRuleTriggered(): Observable<any> {
    return this.on('rule_triggered');
  }

  onCompetitorPriceUpdate(): Observable<CompetitorPriceMessage> {
    return this.competitorPrices;
  }

  onSystemAlert(): Observable<SystemAlertMessage> {
    return this.systemAlerts;
  }

  subscribeToProduct(productId: string): void {
    this.subscribeToProducts([productId]);
  }

  unsubscribeFromProduct(productId: string): void {
    this.unsubscribeFromProducts([productId]);
  }

  /**
   * Построить WebSocket URL: из environment (dev) или динамически из location (beta/prod).
   */
  private getWebSocketUrl(): string {
    if (environment.websocketUrl) {
      return environment.websocketUrl;
    }
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${location.host}/ws`;
  }

  private async syncMissedEvents(): Promise<void> {
    if (!this.lastEventId) {
      return;
    }

    try {
      const apiBaseUrl = environment.apiUrl;
      const params = new HttpParams().set('since', this.lastEventId);
      const response = await firstValueFrom(
        this.http.get<DeltaEventsResponse>(`${apiBaseUrl}/events`, { params })
      );

      response.events.forEach((event) => {
        this.handleServerEventEnvelope({
          id: event.id,
          type: 'event',
          event: event.event,
          data: event.data,
          timestamp: event.timestamp
        });
      });
    } catch (error) {
      console.warn('[WS] Delta sync failed', error);
    }
  }

  private isDuplicateEvent(eventId: string): boolean {
    const sequence = Number.parseInt(eventId, 10);
    return Number.isFinite(sequence) && sequence <= this.lastEventSequence;
  }

  private rememberEventId(eventId: string): void {
    this.lastEventId = eventId;

    const sequence = Number.parseInt(eventId, 10);
    if (Number.isFinite(sequence)) {
      this.lastEventSequence = Math.max(this.lastEventSequence, sequence);
    }
  }

  private normalizeOrderCreatedEvent(data: any, timestamp?: string): OrderCreatedEvent {
    return {
      orderId: String(data?.order_id ?? data?.orderId ?? ''),
      externalOrderId: data?.external_order_id ?? data?.externalOrderId,
      marketplace: data?.marketplace,
      totalRevenue: this.toNumber(data?.total_revenue ?? data?.totalRevenue),
      createdAt: data?.created_at ?? data?.createdAt ?? timestamp
    };
  }

  private normalizePriceUpdatedEvent(data: any, timestamp?: string): PriceUpdatedEvent {
    return {
      productId: String(data?.product_id ?? data?.productId ?? ''),
      marketplace: data?.marketplace,
      oldPrice: this.toNumber(data?.old_price ?? data?.oldPrice),
      newPrice: this.toNumber(data?.new_price ?? data?.newPrice) ?? 0,
      currency: data?.currency,
      updatedAt: data?.updated_at ?? data?.updatedAt ?? timestamp
    };
  }

  private normalizeRepricingCompletedEvent(data: any, timestamp?: string): RepricingCompletedEvent {
    return {
      updatedCount: this.toNumber(data?.updated_count ?? data?.updatedCount) ?? 0,
      totalCount: this.toNumber(data?.total_count ?? data?.totalCount),
      completedAt: data?.completed_at ?? data?.completedAt ?? timestamp
    };
  }

  private normalizeCompetitorChangedEvent(data: any, timestamp?: string): CompetitorChangedEvent {
    return {
      productId: String(data?.product_id ?? data?.productId ?? ''),
      competitorName: data?.competitor_name ?? data?.competitorName,
      newPrice: this.toNumber(data?.new_price ?? data?.newPrice),
      changedAt: data?.changed_at ?? data?.changedAt ?? timestamp
    };
  }

  private toNumber(value: unknown): number | undefined {
    if (value == null || value === '') {
      return undefined;
    }

    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
